import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Box, Clock3, Filter, HardDrive, RefreshCw } from 'lucide-react';
import ModelCard from './ModelCard';
import UploadQueueSection from './UploadQueueSection';
import { useModels } from '../../hooks/useModels';
import { formatBytes } from '../../utils';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { Model } from '../../types';

interface ModelsListProps {
  onUploadRequest?: () => void;
}

type StatusFilter = 'all' | 'ready' | 'processing' | 'failed';

const statusLabels: Record<StatusFilter, string> = {
  all: 'All',
  ready: 'Ready',
  processing: 'Processing',
  failed: 'Failed',
};

const ModelsList: React.FC<ModelsListProps> = ({ onUploadRequest }) => {
  const {
    models,
    isLoading,
    error,
    deleteModel,
    renameModel,
    refetch,
    readyModels,
    processingModels,
    failedModels,
  } = useModels();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const navigate = useNavigate();

  const filteredModels = useMemo(() => {
    if (filter === 'all') return models;
    if (filter === 'processing') {
      // Treat "uploaded" as processing for interim states
      return models.filter((m) => m.status === 'processing' || m.status === 'uploaded');
    }
    return models.filter((m) => m.status === filter);
  }, [filter, models]);

  const totalSize = useMemo(
    () => models.reduce((acc, m) => acc + (m.originalFileSize || 0), 0),
    [models]
  );

  const handleView = (model: Model) => {
    navigate(`/models/${model.id}/view`);
  };

  const renderStats = () => (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={<HardDrive className="h-5 w-5 text-primary-600" />}
        label="Total Models"
        value={models.length}
        body={`Ready: ${readyModels.length} • Processing: ${processingModels.length}`}
      />
      <StatCard
        icon={<Clock3 className="h-5 w-5 text-amber-600" />}
        label="In Progress"
        value={processingModels.length}
        body="Uploads still processing"
      />
      <StatCard
        icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
        label="Failed"
        value={failedModels.length}
        body="Processing errors"
      />
      <StatCard
        icon={<Box className="h-5 w-5 text-emerald-600" />}
        label="Storage Used"
        value={formatBytes(totalSize)}
        body={`${models.length} item${models.length === 1 ? '' : 's'}`}
      />
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex h-64 flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white">
        <LoadingSpinner size="large" />
        <p className="mt-3 text-sm text-gray-600">Loading your models...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-red-600" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-700">Could not load models</h3>
            <p className="text-sm text-red-600">
              Please check your connection and try again.
            </p>
            <div className="mt-3 flex gap-2">
              <Button variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (models.length === 0) {
    return (
      <EmptyState
        title="No models uploaded yet"
        message="Upload your first GLB to begin building your library."
        action={
          <Button onClick={onUploadRequest} className="flex items-center gap-2">
            <Box className="h-4 w-4" />
            Upload a model
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload queue — shown above the grid whilst uploads are in progress */}
      <UploadQueueSection />

      {/* Stats */}
      {renderStats()}

      {/* Controls */}
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-600">
          <Filter className="h-4 w-4 flex-shrink-0 text-gray-500" />
          <span className="flex-shrink-0">Filter by status:</span>
          <div className="flex flex-wrap rounded-lg border border-gray-200 overflow-hidden">
            {(Object.keys(statusLabels) as StatusFilter[]).map((key) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1 text-sm transition-colors ${
                  filter === key
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {statusLabels[key]}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-start gap-2 text-xs text-gray-500 sm:flex-row sm:items-center">
          <span>Auto-refresh only while processing. Last updated just now.</span>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Grid */}
      {filteredModels.length === 0 ? (
        <EmptyState
          message="No models match this status. Try another filter."
          action={
            <Button variant="secondary" onClick={() => setFilter('all')}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredModels.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
              onDelete={deleteModel}
              onView={handleView}
              onRename={(id, name) => renameModel(id, name).then(() => undefined)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  body?: string;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, body }) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50">
        {icon}
      </div>
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
        {body && <p className="text-xs text-gray-500">{body}</p>}
      </div>
    </div>
  </div>
);

export default ModelsList;
